import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, SimpleGrid, Spinner,
  Badge, Icon, Divider, Alert, AlertIcon, Wrap, WrapItem,
} from '@chakra-ui/react';
import { FiRefreshCw, FiDatabase, FiCpu, FiLink, FiMessageSquare } from 'react-icons/fi';

const API_BASE = '/api/v1';

function HealthCheck() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('assistant_token')}`,
  });

  const fetchHealth = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/health/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => { setHealth(data); setLastChecked(new Date()); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const statusColor = (status) => {
    switch (status) {
      case 'healthy': case 'connected': case 'complete': return 'green';
      case 'degraded': case 'incomplete': return 'orange';
      case 'not_configured': case 'offline': return 'yellow';
      case 'error': case 'unhealthy': return 'red';
      default: return 'gray';
    }
  };

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" color="purple.500" /></Box>;
  if (!health) return <Alert status="error"><AlertIcon />Failed to load health check data</Alert>;

  const checks = health.checks || {};

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Health Check</Heading>
          <Text color="gray.500">
            Service connectivity and status
            {lastChecked && ` — last checked ${lastChecked.toLocaleTimeString()}`}
          </Text>
        </VStack>
        <Button leftIcon={<FiRefreshCw />} colorScheme="purple" onClick={fetchHealth} isLoading={loading}>Re-check</Button>
      </HStack>

      <Alert
        status={health.overall === 'healthy' ? 'success' : health.overall === 'degraded' ? 'warning' : 'error'}
        borderRadius="lg" mb={6} py={4}
      >
        <AlertIcon boxSize={6} />
        <VStack align="start" spacing={0} ml={2}>
          <Text fontWeight="bold" fontSize="lg">
            Overall: <Badge colorScheme={statusColor(health.overall)} fontSize="md" px={2}>{health.overall}</Badge>
          </Text>
        </VStack>
      </Alert>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
        {/* Database */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack mb={4}>
            <Icon as={FiDatabase} boxSize={6} color="blue.500" />
            <Heading size="md">Database</Heading>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.database?.status)} fontSize="sm">{checks.database?.status || 'unknown'}</Badge>
            </HStack>
            {checks.database?.latency_ms != null && (
              <HStack justify="space-between">
                <Text color="gray.600">Latency</Text>
                <Text fontWeight="bold">{checks.database.latency_ms} ms</Text>
              </HStack>
            )}
            {checks.database?.message && (
              <Alert status="error" borderRadius="md" fontSize="sm"><AlertIcon />{checks.database.message}</Alert>
            )}
          </VStack>
        </Box>

        {/* LLM backend — self-hosted Ollama or a managed provider, whichever is active */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack mb={4} justify="space-between">
            <HStack>
              <Icon as={FiCpu} boxSize={6} color="purple.500" />
              <Heading size="md">LLM Backend</Heading>
            </HStack>
            {checks.llm?.provider && (
              <Badge colorScheme="gray" fontSize="xs">{checks.llm.provider === 'managed' ? 'Managed' : 'Self-hosted'}</Badge>
            )}
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.llm?.status)} fontSize="sm">{checks.llm?.status || 'unknown'}</Badge>
            </HStack>
            {checks.llm?.latency_ms != null && (
              <HStack justify="space-between">
                <Text color="gray.600">Latency</Text>
                <Text fontWeight="bold">{checks.llm.latency_ms} ms</Text>
              </HStack>
            )}
            {checks.llm?.model && (
              <HStack justify="space-between" align="start">
                <Text color="gray.600">Model</Text>
                <Text fontWeight="bold" fontSize="sm" textAlign="right" maxW="60%">{checks.llm.model}</Text>
              </HStack>
            )}
            {checks.llm?.provider === 'managed' && checks.llm?.base_url && (
              <HStack justify="space-between" align="start">
                <Text color="gray.600">Endpoint</Text>
                <Text fontSize="xs" textAlign="right" maxW="60%" isTruncated>{checks.llm.base_url}</Text>
              </HStack>
            )}
            {checks.llm?.message && (
              <Alert status="warning" borderRadius="md" fontSize="sm"><AlertIcon />{checks.llm.message}</Alert>
            )}
          </VStack>
        </Box>

        {/* api/control dependency */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack mb={4}>
            <Icon as={FiLink} boxSize={6} color="orange.500" />
            <Heading size="md">Control API</Heading>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.control_api?.status)} fontSize="sm">{checks.control_api?.status || 'unknown'}</Badge>
            </HStack>
            {checks.control_api?.latency_ms != null && (
              <HStack justify="space-between">
                <Text color="gray.600">Latency</Text>
                <Text fontWeight="bold">{checks.control_api.latency_ms} ms</Text>
              </HStack>
            )}
            {checks.control_api?.message && (
              <Alert status="error" borderRadius="md" fontSize="sm"><AlertIcon />{checks.control_api.message}</Alert>
            )}
          </VStack>
        </Box>

        {/* Chat activity */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack mb={4}>
            <Icon as={FiMessageSquare} boxSize={6} color="teal.500" />
            <Heading size="md">Chat Activity</Heading>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.chat?.status)} fontSize="sm">{checks.chat?.status || 'unknown'}</Badge>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.600">Sessions</Text>
              <Text fontWeight="bold">{checks.chat?.total_sessions ?? 0}</Text>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.600">Messages</Text>
              <Text fontWeight="bold">{checks.chat?.total_messages ?? 0}</Text>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.600">Sessions (24h)</Text>
              <Text fontWeight="bold">{checks.chat?.sessions_24h ?? 0}</Text>
            </HStack>
            {checks.chat?.message && (
              <Alert status="warning" borderRadius="md" fontSize="sm"><AlertIcon />{checks.chat.message}</Alert>
            )}
          </VStack>
        </Box>
      </SimpleGrid>

      {checks.llm?.provider === 'ollama' && checks.llm?.models_available?.length > 0 && (
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" mt={6}>
          <Heading size="sm" mb={3}>Models Available on Ollama</Heading>
          <Wrap>
            {checks.llm.models_available.map((m) => (
              <WrapItem key={m}>
                <Badge colorScheme={m === checks.llm.model ? 'purple' : 'gray'} px={2} py={1} borderRadius="md">{m}</Badge>
              </WrapItem>
            ))}
          </Wrap>
        </Box>
      )}
    </Box>
  );
}

export default HealthCheck;
